import { ChangeEvent, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useHelperStore, useTabStore } from "~/stores";
import { SEARCH_TABS, SEARCH_VIEW_MODE } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";
import useDebounce from "~/hooks/useDebounce";

import { XCircleIcon } from "@heroicons/react/24/solid";

const SearchKeywordInput = () => {
    const { debouncedText: searchText, setText } = useDebounce({});
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);

    const dispatch = useHelperStore(state => state.dispatch);
    const viewMode = useHelperStore(state => state.viewMode);
    const activeTab = useTabStore(state => state.tab);
    const isTopicSearch = activeTab?.key !== SEARCH_TABS.FULL_DOCUMENTS;
    const searchInputLabel = isTopicSearch
        ? t("searchForm.popup.searchInputLabelTopic")
        : t("searchForm.popup.searchInputLabelCompany");

    useEffect(() => {
        if (searchText.length >= 1 && viewMode !== SEARCH_VIEW_MODE.KEYWORD) {
            dispatch({
                type: SearchActions.SET_SEARCH_VIEW_MODE,
                payload: SEARCH_VIEW_MODE.KEYWORD
            });
        }

        dispatch({ type: SearchActions.SET_TOPIC_KEYWORD, payload: searchText });
    }, [searchText]);

    const handleBtnClick = (open: boolean) => {
        const topicsViewMode = open ? SEARCH_VIEW_MODE.KEYWORD : SEARCH_VIEW_MODE.SELECTION;
        dispatch({ type: SearchActions.SET_SEARCH_VIEW_MODE, payload: topicsViewMode });

        if (!open && inputRef.current) {
            inputRef.current.value = "";
            setText("");
        }
    };

    const handleKeywordChange = (e: ChangeEvent<HTMLInputElement>) => {
        setText(e.target.value);
    };

    return (
        <form className="relative w-full h-17" autoComplete="off">
            <input
                type="text"
                className="w-full py-3.5 px-4 rounded-2xl bg-stone-lighter text-base text-stone-darkest font-medium-light outline-none outline-stone-medium border-red-500"
                placeholder={searchInputLabel}
                autoComplete="off"
                ref={inputRef}
                onChange={handleKeywordChange}
                onClick={() => handleBtnClick(true)}
            />
            {viewMode === SEARCH_VIEW_MODE.KEYWORD && (
                <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1"
                    onClick={() => handleBtnClick(false)}
                >
                    <XCircleIcon className="w-6 h-6 text-stone-medium" />
                </button>
            )}
        </form>
    );
};

export default SearchKeywordInput;
